import React, { useCallback, useMemo } from 'react';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import COLORS from '@/resources/style/Themes';
import { getColorForGroup } from '@/utils/colorUtils';
import { t } from 'i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useLocationSharingActions } from '@/stores/map/locationSharingStore';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
} from '@vis.gl/react-google-maps';

/* global google */

const LocationSharingMarker = ({
  position,
  userAuthId,
  isEmergencyResponder,
  emergencyCreatorPositions,
  groupName,
}) => {
  const { groups } = useOrganizationUpdaterProvider();
  const locationSharingActions = useLocationSharingActions();
  const dashboardActions = useDashboardActions();

  const calculateDistance = () => {
    if (
      !emergencyCreatorPositions ||
      emergencyCreatorPositions.length === 0 ||
      !position
    )
      return null;

    const distances = emergencyCreatorPositions.map((emergencyPosition) => {
      const earthRadius = 6371e3;
      const currentLatRad = (position.lat * Math.PI) / 180;
      const emergencyLatRad = (emergencyPosition.lat * Math.PI) / 180;
      const latDiffRad =
        ((emergencyPosition.lat - position.lat) * Math.PI) / 180;
      const lngDiffRad =
        ((emergencyPosition.lng - position.lng) * Math.PI) / 180;
      const a =
        Math.sin(latDiffRad / 2) * Math.sin(latDiffRad / 2) +
        Math.cos(currentLatRad) *
          Math.cos(emergencyLatRad) *
          Math.sin(lngDiffRad / 2) *
          Math.sin(lngDiffRad / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return earthRadius * c;
    });
    const minDistance = Math.min(...distances);
    if (minDistance < 1609.34)
      return `${Math.round(minDistance * 3.28084)} ${t(StringKeys.FEET)}`;
    else return `${(minDistance / 1609.34).toFixed(1)} ${t(StringKeys.MILES)}`;
  };

  const getUserInfo = useCallback(() => {
    if (!groups) return null;

    const group = groups.find((group) => group.group_name === groupName);
    if (!group) return null;
    const userDetails = group.group_value?.find(
      (user) => user.auth_id === userAuthId,
    );
    if (userDetails) {
      const info = {
        groupName: group.group_name,
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        phoneNumber: userDetails.phone_number,
        role: userDetails.role,
        userAuthId: userAuthId,
      };
      return info;
    }
    return null;
  }, [groups, groupName, userAuthId]);

  const userInfo = useMemo(() => getUserInfo(), [getUserInfo]);

  const markerColor = useMemo(
    () =>
      isEmergencyResponder
        ? COLORS.SAFEGUARD_BLUE
        : userInfo
          ? getColorForGroup(userInfo.groupName)
          : COLORS.SAFEGUARD_BLUE,
    [isEmergencyResponder, userInfo],
  );

  const createMarkerSVG = (color) => {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 395.71 395.71">
        <g>
          <path
            d="M197.849,0C122.131,0,60.531,61.609,60.531,137.329c0,72.887,124.591,243.177,129.896,250.388l4.951,6.738
            c0.579,0.792,1.501,1.255,2.471,1.255c0.985,0,1.901-0.463,2.486-1.255l4.948-6.738c5.308-7.211,129.896-177.501,129.896-250.388
            C335.179,61.609,273.569,0,197.849,0z"
            fill="${color}"
            stroke-width="${isEmergencyResponder ? '16' : '12'}"
          />
          <circle
            cx="197.849"
            cy="137.329"
            r="100"
            fill="#FFFFFF"
            stroke="${color}"
            stroke-width="${isEmergencyResponder ? '12' : '8'}"
          />
        </g>
      </svg>
    `;
  };

  const initials = userInfo
    ? `${userInfo.firstName.charAt(0).toUpperCase()}${userInfo.lastName.charAt(0).toUpperCase()}`
    : 'LS';
  const svgString = createMarkerSVG(markerColor);
  const encodedSvg = encodeURIComponent(svgString);

  const icon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodedSvg}`,
    scaledSize: new google.maps.Size(
      isEmergencyResponder ? 60 : 50,
      isEmergencyResponder ? 60 : 50,
    ),
    anchor: new google.maps.Point(
      isEmergencyResponder ? 30 : 25,
      isEmergencyResponder ? 60 : 50,
    ),
    labelOrigin: new google.maps.Point(
      isEmergencyResponder ? 30 : 25,
      isEmergencyResponder ? 25 : 18,
    ),
  };

  if (!position || !position.lat || !position.lng) return null;

  return (
    <>
      <AdvancedMarker
        position={position}
        onClick={() => {
          locationSharingActions.setSelectedLocationMarkerByUserId(userAuthId);
          // Also open the dashboard
          dashboardActions.openDashboard(userAuthId);
        }}
        anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
      >
        <div className="marker">
          <img className="marker-img" src={icon.url} alt="" />
          <span className="marker-initials">{initials}</span>
        </div>
      </AdvancedMarker>
    </>
  );
};

LocationSharingMarker.displayName = 'LocationSharingMarker';

export default LocationSharingMarker;
